<script lang="ts" setup>
  import { computed, ref, watch } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { useStorage } from '@vueuse/core'
  import { preferredLocale } from '@/plugins/i18n'
  import { addColumns } from '@/store/mutations'
  import type { Column } from '@/types'
  import { useNotify } from '@/composables/useNotify'
  import { trackEvent } from '@/lib/track'
  import { ONBOARDING_DEFAULTS } from '@/config/deck/onboarding'
  import {
    getTopicsForLocale,
    buildTopicColumnParams
  } from '@/config/deck/topics'
  import { XMarkIcon } from '@heroicons/vue/24/outline'

  const props = defineProps<{
    open: boolean
  }>()

  const emit = defineEmits<{
    (e: 'update:open', value: boolean): void
    (e: 'completed'): void
    (e: 'skipped'): void
  }>()

  const { t } = useI18n({ useScope: 'global' })
  const notify = useNotify()
  const dialogRef = ref<HTMLDialogElement | null>(null)

  const selectedDefaults = ref<Set<string>>(
    new Set(ONBOARDING_DEFAULTS.map(d => d.id))
  )
  const selectedTopics = ref<Set<string>>(new Set())
  const submitting = ref(false)

  const topics = computed(() => getTopicsForLocale(preferredLocale.value))

  const totalSelected = computed(
    () => selectedDefaults.value.size + selectedTopics.value.size
  )

  watch(
    () => props.open,
    val => {
      if (val) {
        selectedDefaults.value = new Set(ONBOARDING_DEFAULTS.map(d => d.id))
        selectedTopics.value = new Set()
        dialogRef.value?.showModal()
      } else {
        dialogRef.value?.close()
      }
    }
  )

  function toggleDefault(id: string) {
    if (selectedDefaults.value.has(id)) selectedDefaults.value.delete(id)
    else selectedDefaults.value.add(id)
    selectedDefaults.value = new Set(selectedDefaults.value)
  }

  function toggleTopic(value: string) {
    if (selectedTopics.value.has(value)) selectedTopics.value.delete(value)
    else selectedTopics.value.add(value)
    selectedTopics.value = new Set(selectedTopics.value)
  }

  function close() {
    emit('update:open', false)
    dialogRef.value?.close()
  }

  async function submit() {
    if (totalSelected.value === 0 || submitting.value) return
    submitting.value = true
    try {
      const lang = preferredLocale.value
      const payloads: Partial<Column>[] = []
      for (const def of ONBOARDING_DEFAULTS) {
        if (!selectedDefaults.value.has(def.id)) continue
        payloads.push({
          type: def.type,
          params: def.buildParams(lang) as never
        })
      }
      for (const topicValue of selectedTopics.value) {
        payloads.push({
          type: 'topic',
          params: buildTopicColumnParams(lang, topicValue) as never
        })
      }
      await addColumns(payloads)
      trackEvent('onboarding_completed')
      emit('completed')
      close()
    } catch (e) {
      notify.error(e instanceof Error ? e.message : String(e))
    } finally {
      submitting.value = false
    }
  }

  function skip() {
    trackEvent('onboarding_dismissed')
    emit('skipped')
    close()
  }
</script>

<template>
  <dialog
    ref="dialogRef"
    class="modal"
    @close="emit('update:open', false)"
  >
    <div class="modal-box max-h-[90vh] max-w-2xl">
      <button
        type="button"
        class="btn btn-sm btn-ghost btn-circle absolute top-3 right-3"
        :aria-label="t('onboarding.templates.close')"
        @click="skip"
      >
        <XMarkIcon class="size-5" />
      </button>

      <h2 class="text-2xl font-bold">
        {{ t('onboarding.templates.title') }}
      </h2>
      <p class="text-base-content/70 mt-2 mb-4 text-sm">
        {{ t('onboarding.templates.subtitle') }}
      </p>

      <section class="mb-5">
        <h3
          class="text-base-content/80 mb-2 text-xs font-semibold tracking-wide uppercase"
        >
          {{ t('onboarding.templates.recommended') }}
        </h3>
        <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <button
            v-for="def in ONBOARDING_DEFAULTS"
            :key="def.id"
            type="button"
            class="card card-compact bg-base-200 hover:bg-base-300 cursor-pointer border-2 text-left transition-colors"
            :class="
              selectedDefaults.has(def.id)
                ? 'border-primary'
                : 'border-transparent'
            "
            :aria-pressed="selectedDefaults.has(def.id)"
            @click="toggleDefault(def.id)"
          >
            <div class="card-body flex-row items-center gap-3">
              <span
                :class="`icon-[${def.icon}] size-7 shrink-0`"
                aria-hidden="true"
              ></span>
              <div class="flex-1">
                <h4 class="card-title text-base">
                  {{ t(def.labelKey) }}
                </h4>
                <p class="text-base-content/70 text-xs">
                  {{ t(def.descriptionKey) }}
                </p>
              </div>
              <input
                type="checkbox"
                class="checkbox checkbox-primary pointer-events-none"
                :checked="selectedDefaults.has(def.id)"
                tabindex="-1"
              />
            </div>
          </button>
        </div>
      </section>

      <section
        v-if="topics.length"
        class="mb-5"
      >
        <h3
          class="text-base-content/80 mb-2 text-xs font-semibold tracking-wide uppercase"
        >
          {{ t('onboarding.templates.topics') }}
        </h3>
        <div class="flex flex-wrap gap-2">
          <button
            v-for="topic in topics"
            :key="topic.value"
            type="button"
            class="btn btn-sm gap-2"
            :class="
              selectedTopics.has(topic.value) ? 'btn-primary' : 'btn-outline'
            "
            :aria-pressed="selectedTopics.has(topic.value)"
            @click="toggleTopic(topic.value)"
          >
            <span
              :class="`icon-[${topic.icon}] size-4`"
              aria-hidden="true"
            ></span>
            {{ topic.label }}
          </button>
        </div>
      </section>

      <div class="modal-action mt-6 flex-col-reverse gap-2 sm:flex-row">
        <button
          type="button"
          class="btn btn-ghost"
          @click="skip"
        >
          {{ t('onboarding.templates.skip') }}
        </button>
        <button
          type="button"
          class="btn btn-primary"
          :disabled="totalSelected === 0 || submitting"
          @click="submit"
        >
          <span
            v-if="submitting"
            class="loading loading-spinner loading-sm"
          ></span>
          {{
            totalSelected === 0
              ? t('onboarding.templates.submit-empty')
              : t('onboarding.templates.submit', { count: totalSelected })
          }}
        </button>
      </div>
    </div>
    <form
      method="dialog"
      class="modal-backdrop"
    >
      <button
        type="button"
        @click="skip"
      >
        close
      </button>
    </form>
  </dialog>
</template>
